import React from 'react'
import PropTypes from 'prop-types'
import { Spacer, Button, Flex, Modal } from '@jisr-hr/design-system'
import { Typography } from '@jisr-hr/ds'
import { Loader } from 'components/global/JisrLoader/JisrLoader'
import { ReactComponent as WarningIcon } from 'assets/figma-icons/warning-icon.svg'
import { ReactComponent as DeleteWarning } from 'assets/figma-icons/delete-warning-icon.svg'
import { ReactComponent as BlueInfoIcon } from '@jisr-hr/design-system/dist/assets/icons/BlueInfo.svg'
import { ReactComponent as SuccessIcon } from 'assets/figma-icons/success-icon.svg'
import i18n from 'translations/i18n'
import DSIcon from 'components/DSIcon'
import styles from './styles.module.css'

const ICONS_STYLE = {
  width: 40,
  height: 40,
}
// available variants: danger,warning,info
// hAlign => left, center, right
const ConfirmModal = ({
  fetching,
  showLoader = true,
  fullBtnWidth,
  subMessage,
  loadingButton,
  isOpen,
  onCancel,
  onClose,
  onConfirm,
  data,
  message,
  title,
  labelConfirm,
  labelCancel,
  hideConfirm,
  variant,
  width,
  custom,
  hAlign,
  withoutClose,
  customContent,
  disableSubmit,
  hideCancel,
  confirmStartIcon,
  showCloseButton = true,
  modalClassName,
}) => {
  let btnVariant
  const renderIcon = () => {
    switch (variant) {
      case 'danger':
        btnVariant = 'dangerPrimary'
        return <DeleteWarning style={ICONS_STYLE} />
      case 'warning':
        btnVariant = 'warningPrimary'
        return (
          <WarningIcon
            style={ICONS_STYLE}
            className={styles.confirmIcon}
          />
        )
      case 'info':
        btnVariant = 'primary'
        return <BlueInfoIcon />
      case 'success':
        btnVariant = 'primary'
        return <SuccessIcon style={ICONS_STYLE} />
      case 'custom':
        btnVariant = custom?.btnVariant || 'primary'
        return custom?.icon
      default:
        return null
    }
  }

  const textHorizonAlign = {
    left: 'left',
    right: 'right',
    center: 'center',
  }

  const handleOnConfirm = (e) => onConfirm(e, data)

  return (
    <Modal
      disableCloseOut
      isOpen={isOpen}
      variant="centerDialog"
      width={width || 360}
      onClose={onClose || onCancel}
      showCloseButton={showCloseButton}
      modalPaperClassName={modalClassName}
      header={
        !hAlign && (
          <Flex
            itemsCenter
            style={{ gap: 14, width: '100%' }}
          >
            {renderIcon()}
            <Typography
              variant="interface/default/m"
              style={{ color: 'var(--color-base-colors-grey-1000)' }}
              text={i18n.t(title)}
            />
          </Flex>
        )
      }
      modalHeaderClassName={styles.confirmModalHeader}
    >
      <div className={styles.confirmModalContent}>
        {hAlign && (
          <>
            <Flex justifyBetween>
              {renderIcon()}
              {!withoutClose && (
                <DSIcon
                  name="x"
                  color="var(--ds-core-color-gray-500)"
                  size="md"
                  styles={{ cursor: 'pointer' }}
                  onClick={onCancel}
                />
              )}
            </Flex>
            <Spacer height={16} />
            <Typography
              variant="subtitle-1"
              style={{ color: 'var(--color-base-colors-grey-1000)' }}
              text={i18n.t(title)}
            />
            <Spacer height={4} />
          </>
        )}
        <Typography
          variant={subMessage ? 'subtitle-1' : 'body-new/regular'}
          // style={{ maxWidth: 300 }}
          style={{
            ...(hAlign && { textAlign: textHorizonAlign[hAlign] }),
            color: hAlign
              ? 'var(--color-base-colors-grey-700)'
              : 'var(--color-base-colors-black-700)',
          }}
          text={message}
          testId="message"
        />
        {subMessage && (
          <Typography
            style={{
              color: 'var(--color-base-colors-grey-700)',
              marginTop: 4,
            }}
            variant="body-new/regular"
            text={subMessage}
            // style={{ maxWidth: 300 }}
          />
        )}
        {fetching && showLoader && <Loader />}
        {customContent && (
          <>
            <Spacer height={36} />
            {customContent}
          </>
        )}
        <Spacer height={hAlign ? 32 : 24} />
        <Flex
          justifyEnd
          style={{ gap: 8, minWidth: 200 }}
        >
          {!hideCancel && (
            <Button
              disabled={fetching || loadingButton}
              label={i18n.t(labelCancel ?? 'cancel')}
              onClick={onCancel}
              variant="secondary"
              size="medium"
              data-testId="cancel"
              style={{
                ...(hAlign && { width: '50%', justifyContent: 'center' }),
                ...(!hAlign && fullBtnWidth && { flex: 1, justifyContent: 'center' }),
              }}
            />
          )}
          {!hideConfirm && (
            <Button
              disabled={fetching || disableSubmit}
              label={
                loadingButton ? (
                  <DSIcon
                    name="loading-02"
                    spin
                  />
                ) : (
                  i18n.t(labelConfirm)
                )
              }
              onClick={!loadingButton ? handleOnConfirm : () => {}}
              startIcon={confirmStartIcon && <DSIcon name={confirmStartIcon} />}
              size="medium"
              data-testId="confirm"
              variant={custom?.btnVariant || btnVariant}
              style={{
                ...(hAlign && { width: '50%', justifyContent: 'center' }),
                ...(!hAlign && fullBtnWidth && { flex: 1, justifyContent: 'center' }),
              }}
            />
          )}
        </Flex>
      </div>
    </Modal>
  )
}

ConfirmModal.defaultProps = {
  labelConfirm: 'confirm',
  variant: 'danger',
}

ConfirmModal.propTypes = {
  custom: PropTypes.shape(),
  fetching: PropTypes.bool,
  loadingButton: PropTypes.bool,
  hideCancel: PropTypes.bool,
  hideConfirm: PropTypes.bool,
  isOpen: PropTypes.bool,
  withoutClose: PropTypes.bool,
  showLoader: PropTypes.bool,
  onCancel: PropTypes.func,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  message: PropTypes.node,
  variant: PropTypes.string,
  title: PropTypes.string,
  labelCancel: PropTypes.string,
  subMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  hAlign: PropTypes.string,
  labelConfirm: PropTypes.string,
  width: PropTypes.number,
  disableSubmit: PropTypes.bool,
  fullBtnWidth: PropTypes.bool,
  customContent: PropTypes.node,
  data: PropTypes.shape(),
  confirmStartIcon: PropTypes.node,
  showCloseButton: PropTypes.bool,
  modalClassName: PropTypes.string,
}

export default ConfirmModal
